import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Authentication Context
 * Manages global authentication state across the application
 * Uses localStorage for cross-tab/window synchronization
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    email: null,
    loginType: null,
    accessibleInstances: [],
    verificationResult: null
  });

  // Load authentication state from localStorage on mount
  useEffect(() => {
    const loadAuthState = () => {
      try {
        const savedAuthState = localStorage.getItem('authState');
        if (savedAuthState) {
          const parsedState = JSON.parse(savedAuthState);
          console.log('🔐 [AuthContext] Loading saved authentication state:', {
            isAuthenticated: parsedState.isAuthenticated,
            email: parsedState.email,
            loginType: parsedState.loginType
          });
          setAuthState(parsedState);
        } else {
          console.log('ℹ️ [AuthContext] No saved authentication state found');
        }
      } catch (error) {
        console.error('❌ [AuthContext] Error loading auth state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuthState();

    // Create BroadcastChannel for cross-tab communication
    const authChannel = new BroadcastChannel('auth-channel');
    
    authChannel.onmessage = (event) => {
      if (event.data.type === 'LOGOUT') {
        console.log('🚪 [AuthContext] Logout broadcast received from another tab');
        setAuthState({
          isAuthenticated: false,
          user: null,
          email: null,
          loginType: null,
          accessibleInstances: [],
          verificationResult: null
        });
        // Force redirect
        window.location.href = '/';
      }
    };

    // Listen for storage changes from other tabs/windows (fallback)
    const handleStorageChange = (e) => {
      if (e.key === 'authState') {
        if (e.newValue === null) {
          // Logout detected from another tab
          console.log('🚪 [AuthContext] Logout detected via storage event from another tab/window');
          setAuthState({
            isAuthenticated: false,
            user: null,
            email: null,
            loginType: null,
            accessibleInstances: [],
            verificationResult: null
          });
          // Redirect to login page
          window.location.href = '/';
        } else {
          // Login/update detected from another tab
          try {
            const parsedState = JSON.parse(e.newValue);
            console.log('🔄 [AuthContext] Auth state updated from another tab/window');
            setAuthState(parsedState);
          } catch (error) {
            console.error('❌ [AuthContext] Error parsing storage event:', error);
          }
        }
      }
    };

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      authChannel.close();
    };
  }, []);

  // Save authentication state to localStorage whenever it changes
  useEffect(() => {
    if (authState.isAuthenticated) {
      try {
        localStorage.setItem('authState', JSON.stringify(authState));
        console.log('💾 [AuthContext] Authentication state saved to localStorage');
      } catch (error) {
        console.error('❌ [AuthContext] Error saving auth state:', error);
      }
    }
  }, [authState]);

  /**
   * Login function - sets authentication state
   */
  const login = (userData) => {
    console.log('✅ [AuthContext] User logged in:', {
      email: userData.email,
      loginType: userData.loginType,
      instancesCount: userData.accessibleInstances?.length || 0
    });

    const newAuthState = {
      isAuthenticated: true,
      user: userData.user || userData.email,
      email: userData.email,
      loginType: userData.loginType,
      accessibleInstances: userData.accessibleInstances || [],
      verificationResult: userData.verificationResult || null
    };

    setAuthState(newAuthState);
    localStorage.setItem('authState', JSON.stringify(newAuthState));
  };

  /**
   * Logout function - clears authentication state across all tabs
   */
  const logout = () => {
    console.log('🚪 [AuthContext] User logged out - clearing all sessions');
    
    const clearedState = {
      isAuthenticated: false,
      user: null,
      email: null,
      loginType: null,
      accessibleInstances: [],
      verificationResult: null
    };

    setAuthState(clearedState);
    
    // Remove from localStorage - this triggers storage event in other tabs
    localStorage.removeItem('authState');
    
    // Broadcast logout to all tabs
    const authChannel = new BroadcastChannel('auth-channel');
    authChannel.postMessage({ type: 'LOGOUT' });
    authChannel.close();
    
    // Also clear any other auth-related storage
    sessionStorage.removeItem('msalAccount');
    localStorage.removeItem('sapCdcToken');
    
    console.log('🧹 [AuthContext] All authentication data cleared - logout broadcast sent');
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return authState.isAuthenticated;
  };

  /**
   * Get current user data
   */
  const getCurrentUser = () => {
    return {
      user: authState.user,
      email: authState.email,
      loginType: authState.loginType
    };
  };

  /**
   * Get accessible instances
   */
  const getAccessibleInstances = () => {
    return authState.accessibleInstances;
  };

  const value = {
    authState,
    login,
    logout,
    isAuthenticated,
    getCurrentUser,
    getAccessibleInstances,
    isLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
